<template>
  <div class="build-collection">
    <v-btn v-if="email && !welcome" class="collection-trigger" small rounded @click="openCollection">
      <v-icon small left>mdi-bookmark-outline</v-icon> My builds <span v-if="builds.length">&nbsp;({{ builds.length }})</span>
    </v-btn>

    <v-dialog :value="welcome && !initializing" persistent max-width="460" content-class="collection-dialog" aria-labelledby="welcome-title">
      <v-card class="collection-card">
        <div class="collection-art"><img :src="logoUrl" alt="Velodrom" width="210" height="37"><span>BIKE BUILDER</span></div>
        <div class="collection-content">
          <span class="collection-eyebrow">YOUR NEXT RIDE STARTS HERE</span>
          <h2 id="welcome-title">A bike that’s yours.</h2>
          <p>Start with your email. Create your dream bike, save your favourites and pick up where you left off.</p>
          <v-form ref="welcomeForm" @submit.prevent="start">
            <template>
              <v-text-field key="email" v-model="loginEmail" label="Email address" type="email" autocomplete="email" outlined autofocus :disabled="busy" :rules="emailRules" />
              <v-checkbox v-model="marketing" class="collection-consent" :disabled="busy" color="#344638" hide-details>
                <template v-slot:label><span>Email me Velodrom news, new arrivals and offers. I can unsubscribe at any time. <b>(Optional)</b></span></template>
              </v-checkbox>
              <p class="collection-privacy">Your email gives you access to your saved builds. <a href="https://www.velodrom.cc/policies/privacy-policy" target="_blank" rel="noopener">Privacy policy</a></p>
            </template>
            <v-alert v-if="error" dense text type="error" role="alert">{{ error }}</v-alert>
            <v-btn type="submit" class="collection-primary" block large :loading="busy" dark>Start building<v-icon right small>mdi-arrow-right</v-icon></v-btn>
          </v-form>
          <span class="collection-footnote">No password. Just possibilities.</span>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="saveDialog" :persistent="busy" max-width="460" content-class="collection-dialog" aria-labelledby="save-title">
      <v-card class="collection-card collection-content">
        <v-btn icon class="collection-close" aria-label="Close save dialog" :disabled="busy" @click="saveDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        <span class="collection-symbol"><v-icon>mdi-bookmark-outline</v-icon></span>
        <span class="collection-eyebrow">KEEP THE GOOD IDEAS</span>
        <h2 id="save-title">Save the build.</h2>
        <p>Give this one a name. It’ll be waiting whenever you’re ready to ride.</p>
        <div class="collection-summary"><v-icon>mdi-bike</v-icon><div><strong>{{ frameName }}</strong><span>{{ count }} components selected</span></div></div>
        <v-form ref="saveForm" @submit.prevent="save">
          <v-text-field v-model="name" label="Build name" outlined counter="80" maxlength="80" :disabled="busy" :rules="nameRules" autofocus />
          <p class="collection-privacy">Saving to <strong>{{ email }}</strong></p>
          <v-alert v-if="error" dense text type="error" role="alert">{{ error }}</v-alert>
          <v-btn type="submit" class="collection-primary" block large dark :loading="busy">Save the build<v-icon right small>mdi-bookmark-outline</v-icon></v-btn>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="collection" :max-width="comparison ? 760 : 460" :fullscreen="!!comparison && $vuetify.breakpoint.xsOnly" content-class="collection-dialog" aria-labelledby="collection-title">
      <v-card class="collection-card collection-content">
        <CompareBuilds v-if="comparison" :left="comparison[0]" :right="comparison[1]" @back="comparing = false" @swap="picked.reverse()" @close="collection = false" @load="restore" />
        <template v-else>
          <v-btn icon class="collection-close" aria-label="Close previous builds" @click="collection = false"><v-icon>mdi-close</v-icon></v-btn>
          <span class="collection-eyebrow">YOUR PERSONAL COLLECTION</span>
          <h2 id="collection-title">{{ picking ? 'Compare two builds.' : builds.length ? 'Welcome back.' : 'Room for your next ride.' }}</h2>
          <p>{{ picking ? 'Pick two to see price, weight and every part side by side.' : builds.length ? 'Pick up a previous build, or start something new.' : 'Your saved builds will appear here. Choose a frame to get started.' }}</p>
          <button v-if="candidates.length > 1" type="button" class="collection-compare" :aria-pressed="String(picking)" @click="togglePicking">
            <v-icon small>{{ picking ? 'mdi-close' : 'mdi-compare-horizontal' }}</v-icon>{{ picking ? 'Cancel comparison' : 'Compare builds' }}
          </button>
          <div class="collection-list" :class="{ 'is-picking': picking }">
            <button v-for="build in (picking ? candidates : builds)" :key="build.id" class="collection-build" :class="{ 'is-picked': pickIndex(build) >= 0 }"
              :aria-pressed="picking ? String(pickIndex(build) >= 0) : null" @click="picking ? pick(build) : restore(build)">
              <span class="collection-mini"><v-icon>{{ build.current ? 'mdi-pencil-ruler' : 'mdi-bike' }}</v-icon></span>
              <span><strong>{{ build.name }}</strong><small>{{ build.current ? 'On screen now, including unsaved changes' : 'Saved ' + date(build.savedAt) }}</small></span>
              <span v-if="picking" class="collection-pick" aria-hidden="true">{{ pickIndex(build) >= 0 ? 'AB'[pickIndex(build)] : '' }}</span>
              <v-icon v-else>mdi-arrow-right</v-icon>
            </button>
          </div>
          <v-btn v-if="picking" class="collection-primary" block large dark :disabled="picked.length < 2" @click="comparing = true">
            {{ picked.length === 2 ? 'Compare side by side' : `Select ${2 - picked.length} more` }}<v-icon v-if="picked.length === 2" right small>mdi-arrow-right</v-icon>
          </v-btn>
          <v-btn v-else class="collection-primary" block large dark @click="collection = false">{{ count ? 'Keep building' : 'Start a new build' }}</v-btn>
          <button v-if="!picking && count" type="button" class="collection-new" @click="startNew"><v-icon small>mdi-plus</v-icon>Start a new build</button>
          <div class="collection-account"><span>{{ email }}</span><v-btn text x-small :loading="busy" @click="signOut">Use another email</v-btn></div>
          <v-alert v-if="error" dense text type="error">{{ error }}</v-alert>
        </template>
      </v-card>
    </v-dialog>

    <transition name="collection-slide">
      <aside v-if="prompt && !initializing && !welcome && !collection && !saveDialog && !blocked && !covered" class="collection-nudge" role="status" aria-live="polite">
        <v-icon class="mr-3">mdi-bookmark-outline</v-icon>
        <div><strong>Looks like your next ride.</strong><span>Would you like to save the build?</span><button @click="openSave">Save the build <span aria-hidden="true">→</span></button></div>
        <v-btn icon small aria-label="Dismiss save reminder" @click="dismiss"><v-icon small>mdi-close</v-icon></v-btn>
      </aside>
    </transition>
    <v-snackbar v-model="saved" :timeout="4500" color="#344638">Build saved. Ready when you are.<v-btn text @click="saved = false; openCollection()">View builds</v-btn></v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import { getShopifyCustomerEmail } from '@/utils/shopifyCustomer';
import { appBase } from '@/utils/basePath';
import CompareBuilds from './CompareBuilds';
const SESSION_KEY = 'velodrom.builder.session';
export default {
  components: { CompareBuilds },
  props: { composition: String, frameName: String, count: Number, complete: Boolean, blocked: Boolean, covered: Boolean, decode: Function },
  data: () => ({
    welcome: true, initializing: true, loginEmail: '', email: '', marketing: false,
    token: '', builds: [], busy: false, error: '', saveDialog: false, collection: false,
    name: '', prompt: false, prompted: false, saved: false, pendingSave: false, picking: false, picked: [], comparing: false,
    triedStart: false,
    nameRules: [v => !!(v || '').trim() && v.trim().length <= 80 || 'Give your build a name (up to 80 characters).']
  }),
  computed: {
    logoUrl() { return `${appBase}/velodrom-logo.png`; },
    // An empty field only counts as an error once the visitor tries to start,
    // so focus leaving the field (or a click beside the dialog) doesn't scold.
    emailRules() {
      return [v => {
        const value = (v || '').trim();
        if (!value && !this.triedStart) return true;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Enter a valid email address.';
      }];
    },
    candidates() {
      const current = this.count ? [{ id: 'current', name: 'Current build', composition: this.composition, current: true }] : [];
      return current.concat(this.builds);
    },
    comparison() {
      if (!this.comparing || !this.decode) return null;
      const builds = this.picked.map(id => this.candidates.find(build => build.id === id)).filter(Boolean);
      return builds.length === 2 ? builds.map(build => ({ ...build, selection: this.decode(build.composition) || [] })) : null;
    }
  },
  watch: {
    complete: { immediate: true, handler(value) { if (value) this.schedulePrompt(); } },
    count(value) { if (!value) { this.prompted = false; this.prompt = false; clearTimeout(this.promptTimer); } },
    welcome(value) { this.$emit('gate', value); if (!value && this.complete) this.schedulePrompt(); },
    collection(value) { if (!value) { this.picking = false; this.comparing = false; this.picked = []; } }
  },
  async mounted() {
    this.$emit('gate', true);
    try { this.token = sessionStorage.getItem(SESSION_KEY) || ''; } catch { /* Memory-only session when storage is unavailable. */ }
    this.busy = true;
    try {
      const shopEmail = await getShopifyCustomerEmail();
      let existing = null;
      if (this.token) {
        try { existing = (await this.request('get', 'builds')).data; }
        catch (e) { if (!e.response || e.response.status !== 401) throw e; }
      }
      // The current storefront customer takes precedence over an older builder session.
      if (shopEmail && (!existing || existing.email !== shopEmail)) {
        const { data } = await this.request('post', 'start', { email: shopEmail });
        this.token = data.token;
        try { sessionStorage.setItem(SESSION_KEY, this.token); } catch { /* Optional storage. */ }
        existing = data;
      }
      if (existing) {
        this.email = existing.email; this.builds = existing.builds;
        this.welcome = false; this.collection = !!this.builds.length;
      }
    } catch (e) { this.handleError(e); }
    finally { this.busy = false; this.initializing = false; }
  },
  beforeDestroy() { clearTimeout(this.promptTimer); },
  methods: {
    request(method, path, data) { return axios({ method, url: '/api/builder/' + path, data, headers: { 'X-Builder-Token': this.token }, timeout: 20000 }); },
    handleError(e) {
      this.error = e.response && e.response.data && e.response.data.message || 'We couldn’t connect. Please try again.';
      if (e.response && e.response.status === 401) {
        this.pendingSave = this.saveDialog; this.saveDialog = false; this.collection = false; this.welcome = true; this.token = '';
        try { sessionStorage.removeItem(SESSION_KEY); } catch { /* Optional storage. */ }
      }
    },
    async start() {
      this.triedStart = true;
      if (this.busy || !this.$refs.welcomeForm.validate()) return;
      this.busy = true; this.error = '';
      try {
        this.loginEmail = this.loginEmail.trim().toLowerCase();
        const { data } = await this.request('post', 'start', { email: this.loginEmail, marketing: this.marketing });
        this.token = data.token; this.email = data.email; this.builds = data.builds; this.welcome = false;
        try { sessionStorage.setItem(SESSION_KEY, this.token); } catch { /* Sign-in still works in memory. */ }
        if (this.pendingSave) { this.pendingSave = false; this.openSave(); } else this.collection = !!this.builds.length;
      } catch (e) { this.handleError(e); } finally { this.busy = false; }
    },
    openSave() {
      if (!this.count) return;
      if (this.welcome) { this.pendingSave = true; return; }
      this.error = ''; this.collection = false; this.prompt = false;
      const previous = this.builds.find(b => b.composition === this.composition);
      this.name = previous ? previous.name : this.frameName + ' — My build'; this.saveDialog = true;
    },
    async save() {
      if (this.busy || !this.$refs.saveForm.validate()) return;
      this.busy = true; this.error = '';
      try {
        const { data } = await this.request('post', 'builds', { name: this.name.trim(), composition: this.composition });
        this.builds = data.builds; this.saveDialog = false; this.prompt = false; this.prompted = true; this.saved = true;
      } catch (e) { this.handleError(e); } finally { this.busy = false; }
    },
    openCollection() { this.error = ''; this.collection = true; },
    togglePicking() { this.picking = !this.picking; this.picked = []; },
    pickIndex(build) { return this.picked.indexOf(build.id); },
    pick(build) {
      const index = this.pickIndex(build);
      if (index >= 0) this.picked.splice(index, 1);
      else this.picked = this.picked.concat(build.id).slice(-2);
    },
    // A clean builder; the build on screen stays in the list if it was saved.
    startNew() { this.collection = false; this.prompt = false; this.$emit('new'); },
    restore(build) { this.collection = false; this.prompt = false; this.prompted = true; this.$emit('restore', build.composition); },
    schedulePrompt() {
      clearTimeout(this.promptTimer);
      this.promptTimer = setTimeout(() => {
        if (this.complete && !this.welcome && !this.prompted && !this.builds.some(b => b.composition === this.composition)) { this.prompt = true; this.prompted = true; }
      }, 1800);
    },
    dismiss() { this.prompt = false; },
    date(value) { return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); },
    async signOut() {
      this.busy = true;
      try {
        await this.request('post', 'logout');
        try { sessionStorage.removeItem(SESSION_KEY); } catch { /* Optional storage. */ }
        this.token = ''; this.email = ''; this.loginEmail = ''; this.marketing = false; this.builds = []; this.collection = false; this.welcome = true; this.triedStart = false; this.error = '';
      } catch (e) { this.handleError(e); } finally { this.busy = false; }
    }
  }
};
</script>

<style lang="scss">
.collection-dialog { border-radius: 20px !important; box-shadow: 0 28px 90px #101c2240 !important; }
.collection-card { color: #202720 !important; border-radius: 20px !important; }
.collection-art { height: 112px; background: radial-gradient(ellipse at 70% 10%, #65755b, transparent 65%), #344638; display: flex; align-items: center; justify-content: space-between; padding: 25px 30px; color: #fff; }
.collection-art img { width: 210px; max-width: 65%; height: auto; object-fit: contain; filter: brightness(0) invert(1); }
.collection-art span { max-width: 80px; font-size: 10px; letter-spacing: .18em; line-height: 1.8; }
.collection-content { padding: 30px !important; position: relative; }
.collection-eyebrow { display: block; font-size: 10px; font-weight: 700; letter-spacing: .16em; color: #73816d; margin: 4px 30px 12px 0; }
.collection-content h2 { text-transform: none !important; font-size: 30px; letter-spacing: -.035em; font-weight: 600; line-height: 1.12; margin-bottom: 14px; }
.collection-content p { font-size: 14px; color: #697069; line-height: 1.6; margin-bottom: 22px; }
.collection-content .v-text-field--outlined fieldset { border-color: #d9dfd5; border-radius: 10px; }
.collection-content .v-input { font-size: 16px; }
.collection-content .collection-primary { background: #344638 !important; border-radius: 10px; letter-spacing: .02em; text-transform: none; box-shadow: none; }
.collection-content .collection-consent { margin-top: -5px; margin-bottom: 18px; align-items: flex-start; }
.collection-consent .v-label { font-size: 12px; line-height: 1.5; color: #687168; }
.collection-consent b { font-weight: 400; }
.collection-content .collection-privacy { font-size: 11px; margin: 0 0 18px; overflow-wrap: anywhere; }
.collection-privacy a { color: #344638; text-decoration: underline; }
.collection-footnote { display: block; text-align: center; margin-top: 17px; font-size: 11px; color: #858b80; }
.collection-close { position: absolute !important; right: 14px; top: 14px; }
.collection-content .collection-new { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; height: 44px; margin-top: 8px; border: 1px solid #d9dfd5; border-radius: 10px; color: #344638; font-size: 14px; }
.collection-new .v-icon { color: inherit !important; }
.collection-new:focus-visible { outline: 2px solid #344638; outline-offset: 2px; }
.collection-symbol { display: flex; width: 46px; height: 46px; align-items: center; justify-content: center; border-radius: 13px; background: #edf0e7; margin-bottom: 20px; }
.collection-summary { display: flex; gap: 15px; padding: 16px; background: #f4f5f0; border-radius: 12px; margin-bottom: 22px; }
.collection-summary strong, .collection-summary span { display: block; font-size: 13px; }
.collection-summary span { font-size: 11px; color: #7b8375; margin-top: 4px; }
.collection-list { max-height: 310px; overflow-y: auto; overflow-x: hidden; margin: 0 0 22px; }
.collection-build { display: flex; align-items: center; gap: 14px; padding: 13px 0; width: 100%; border-bottom: 1px solid #e9ece4; text-align: left; }
.collection-build:hover { background: #f5f6f1; }
.collection-build > span:nth-child(2) { flex: 1; min-width: 0; }
.collection-build strong { display: block; font-size: 14px; overflow-wrap: anywhere; }
.collection-build small { display: block; font-size: 11px; color: #7b8375; margin-top: 4px; }
.collection-mini { background: #f0f2e9; padding: 12px; border-radius: 10px; }
.collection-content .collection-compare { display: inline-flex; align-items: center; gap: 6px; margin: -8px 0 6px; padding: 8px 14px; border-radius: 999px; background: #eef1e7; color: #344638; font-size: 12px; font-weight: 700; }
.collection-compare .v-icon { color: inherit !important; }
.collection-list.is-picking .collection-build { padding-left: 8px; padding-right: 8px; border-radius: 10px; }
.collection-build.is-picked { background: #f4f5f0; box-shadow: inset 3px 0 #344b35; }
.collection-pick { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 26px; height: 26px; border-radius: 8px; border: 1.5px solid #c9d0c2; font-size: 12px; font-weight: 700; color: #fff; }
.is-picked .collection-pick { border-color: #344638; background: #344638; }
.v-dialog--fullscreen.collection-dialog, .v-dialog--fullscreen .collection-card { border-radius: 0 !important; }
.v-dialog--fullscreen > .collection-card.collection-content { min-height: 100%; padding: 16px 16px 28px !important; }
.collection-account { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 16px; font-size: 11px; color: #7b8375; }
.collection-account > span { overflow-wrap: anywhere; min-width: 0; }
.build-collection .collection-trigger { position: relative; text-transform: none; letter-spacing: 0; background: #eef1e7 !important; color: #344638; box-shadow: none; }
.collection-nudge { position: fixed; z-index: 7; bottom: 100px; left: 24px; display: flex; align-items: flex-start; max-width: calc(100vw - 32px); padding: 20px; border: 1px solid #dfe5d9; border-radius: 16px; background: #fff; box-shadow: 0 10px 40px #23341b1a; }
.collection-nudge strong, .collection-nudge div > span { display: block; font-size: 13px; }
.collection-nudge div > span { margin: 5px 0 10px; color: #737b6d; font-size: 12px; }
.collection-nudge button:not(.v-btn) { font-size: 12px; color: #344638; font-weight: 700; }
.collection-slide-enter-active, .collection-slide-leave-active { transition: opacity .2s, transform .2s; }
.collection-slide-enter, .collection-slide-leave-to { opacity: 0; transform: translateY(12px); }
@media(max-width:700px) {
  .collection-content { padding: 24px !important; }
  .collection-content h2 { text-transform: none !important; font-size: 27px; }
  .collection-nudge { bottom: 62px; left: 16px; padding: 16px; }
}
@media(prefers-reduced-motion: reduce) { .collection-slide-enter-active, .collection-slide-leave-active { transition: none; } }
@media print { .build-collection { display: none !important; } }
</style>
